// Country-specific content for the landing network
// Positioning: "An Italian on your side" — not "written-first", not "bilingual team".
// Deontology: no "Specialist/Specialized", no 100% guarantees, no self-laudatory claims (art. 35 CDF),
// no national stereotypes, no implied multi-desk structure ("international practice", solo studio).
export const COUNTRIES = {
  uae: {
    code: 'uae',
    label: 'United Arab Emirates',
    short: 'UAE',
    accent: '#C2A878',
    accentClass: 'accent-uae',
    hero: {
      overline: 'International practice · UAE',
      title: ['Recovering claims in Italy', 'for Emirati businesses.'],
      lede:
        'When your Emirati company faces an unpaid Italian counterparty, jurisdiction, procedure and enforcement will often sit in Italy — subject to any jurisdiction or arbitration clause in your contract. Our practice is Italian. We know the tribunals, the officers, the cadence.',
    },
    bottleneck:
      'Between Dubai and Verona sit legalised and sworn translations, rigid court calendars and enforcement officers who operate on Italian time. We absorb the file, prepare each step to the standard Italian tribunals expect, and pursue enforcement locally.',
    trust: {
      title: 'Why instruct an Italian lawyer',
      body:
        'Free-zone entities and mainland companies from Dubai, Abu Dhabi and Sharjah conclude supply, engineering and hospitality contracts with Italian counterparts. When payment discipline breaks down, the file is Italian. Our practice is Italian — Verona-based, handling enforcement proceedings before the Italian tribunals.',
      bullets: [
        'Enforcement proceedings before Italian courts (esecuzione forzata)',
        'Coordination with DIFC / ADGM counsel where required',
        'Every document protected by Italian professional secrecy',
      ],
    },
  },
  usa: {
    code: 'usa',
    label: 'United States',
    short: 'US',
    accent: '#0A3161',
    accentClass: 'accent-usa',
    hero: {
      overline: 'International practice · United States',
      title: ['Recovering claims in Italy', 'for U.S. businesses.'],
      lede:
        "When your U.S. company needs to collect from an Italian debtor, or enforce a U.S. judgment in Italy, the leg that decides the outcome sits in Italy. Our practice is Italian — we handle the Italian side end-to-end.",
    },
    bottleneck:
      'A U.S. judgment does not travel automatically to Italy. Recognition is governed by Italian private international law (Law No. 218/1995) and Italian civil procedure, with document authentication, translation and local filing. We handle the Italian leg — quietly, methodically.',
    trust: {
      title: 'Why instruct an Italian lawyer',
      body:
        'From NY financial firms to California manufacturers to Texas oilfield suppliers, U.S. enterprises regularly trade with Italian counterparts. When payment breaks down or a U.S. judgment needs to be enforced against Italian assets, our practice absorbs the local Italian workflow.',
      bullets: [
        'Recognition and enforcement of foreign judgments in Italy (Law No. 218/1995)',
        'Enforcement proceedings before Italian courts',
        'Every document protected by Italian professional secrecy',
      ],
    },
  },
  uk: {
    code: 'uk',
    label: 'United Kingdom',
    short: 'UK',
    accent: '#8B0000',
    accentClass: 'accent-uk',
    hero: {
      overline: 'International practice · United Kingdom',
      title: ['Recovering claims in Italy', 'for UK businesses.'],
      lede:
        'Post-Brexit, enforcing a UK judgment in Italy is no longer automatic. Our practice is Italian — Verona-based — and handles the Italian recognition and enforcement leg end-to-end.',
    },
    bottleneck:
      'Post-Brexit, English judgments are recognised in Italy under the 2005 Hague Convention on Choice of Court Agreements, the 2019 Hague Judgments Convention for proceedings commenced on or after 1 July 2025, or Italian private international law (Law No. 218/1995) — no longer under Brussels I bis. The file needs preparation to the standard Italian tribunals expect. That is our leg.',
    trust: {
      title: 'Why instruct an Italian lawyer',
      body:
        'City firms, family businesses and industrial suppliers across the UK regularly trade with Italian counterparts. When payment breaks down, or a UK judgment must be enforced against Italian assets, our Italian practice absorbs the local workflow.',
      bullets: [
        'Post-Brexit recognition and enforcement in Italy (Hague 2005 and 2019, Law No. 218/1995)',
        'Enforcement proceedings before Italian courts',
        'Every document protected by Italian professional secrecy',
      ],
    },
  },
  germany: {
    code: 'germany',
    label: 'Germany',
    short: 'Germany',
    accent: '#FFCE00',
    accentClass: 'accent-de',
    nativeGreeting: 'Willkommen.',
    hero: {
      overline: 'International practice · Germany',
      title: ['Recovering claims in Italy', 'for German businesses.'],
      lede:
        "Italy and Germany share one of Europe's busiest trade corridors. When invoices go unpaid or a German judgment must be enforced against Italian assets, our Italian practice handles the local leg — Verona to Milan to Rome.",
    },
    bottleneck:
      'Cross-border files need a written record that your legal department can follow end-to-end. Italian civil procedure moves at its own tempo. We prepare every step so the file remains fully traceable — from the demand to enforcement.',
    trust: {
      title: 'Why instruct an Italian lawyer',
      body:
        'German companies across all sectors conclude significant contracts with Italian counterparts. When payment collapses, our Verona-based practice manages the entire Italian procedural leg.',
      bullets: [
        'Recognition and enforcement under Brussels I bis (EOP, EAPO)',
        'Enforcement proceedings before Italian courts',
        'Every document protected by Italian professional secrecy',
      ],
    },
  },
  greece: {
    code: 'greece',
    label: 'Greece',
    short: 'Greece',
    accent: '#004C97',
    accentClass: 'accent-gr',
    nativeGreeting: 'Καλώς ήρθατε.',
    hero: {
      overline: 'International practice · Greece',
      title: ['Recovering claims in Italy', 'for Greek businesses.'],
      lede:
        'Greek and Italian trade moves fast across the Adriatic. When payment discipline fails and jurisdiction remains Italian, our Italian practice handles the local recovery — from the demand letter to enforcement.',
    },
    bottleneck:
      'Trade between Greece and Italy across the Adriatic is intense. When payment fails, our Italian practice drafts the demand in Italian legal form, keeps you documented, and pursues enforcement before the competent tribunal.',
    trust: {
      title: 'Why instruct an Italian lawyer',
      body:
        'Greek exporters, logistics operators and distributors trade extensively with the Italian market. When payment collapses, the file is Italian. Our practice is Italian — Verona-based, coordinating the entire file before the competent Italian tribunals.',
      bullets: [
        'Recognition and enforcement under Brussels I bis (EOP, EAPO)',
        'Enforcement proceedings before Italian courts',
        'Every document protected by Italian professional secrecy',
      ],
    },
  },
  spain: {
    code: 'spain',
    label: 'Spain / España',
    short: 'Spain',
    accent: '#AA151B',
    accentClass: 'accent-es',
    nativeGreeting: 'Bienvenidos.',
    hero: {
      overline: 'International practice · Spain',
      title: ['Recovering claims in Italy', 'for Spanish businesses.'],
      lede:
        'Spanish and Italian civil law share deep roots, but procedure, timing and enforcement diverge sharply. Our Italian practice handles the Italian leg — coordinated with your abogado when needed.',
    },
    bottleneck:
      "Both jurisdictions descend from Roman law, but the practical procedure in Italy is not what a Spanish abogado expects. We translate the Italian process into terms recognisable to Spanish counsel and drive the file locally.",
    trust: {
      title: 'Why instruct an Italian lawyer',
      body:
        'From Catalan food exporters to Madrid-based industrial suppliers, Spanish businesses trade extensively with the Italian market. When receivables collapse, the file is Italian. Our practice is Italian — we coordinate with your abogado and file locally.',
      bullets: [
        'Recognition and enforcement under Brussels I bis (EOP, EAPO)',
        'Coordination with Spanish counsel where required',
        'Enforcement proceedings before Italian courts',
        'Every document protected by Italian professional secrecy',
      ],
    },
  },
  france: {
    code: 'france',
    label: 'France',
    short: 'France',
    accent: '#0055A4',
    accentClass: 'accent-fr',
    nativeGreeting: 'Bienvenue.',
    hero: {
      overline: 'International practice · France',
      title: ['Recovering claims in Italy', 'for French businesses.'],
      lede:
        "France is among Italy's largest commercial partners. When cross-border payment discipline breaks down, our Italian practice absorbs the local Italian leg and moves it through the tribunals.",
    },
    bottleneck:
      'French counsel operate with structured written record and formal correspondence. Italian tribunals operate on their own cadence. We reconcile the two — with the procedural rigour your legal department expects.',
    trust: {
      title: 'Why instruct an Italian lawyer',
      body:
        'Luxury houses, industrial suppliers and wine producers from Bordeaux, Lyon and Paris routinely trade with Italian counterparts. When receivables collapse, the file is Italian. Our practice is Italian — Verona-based, coordinated with French avocats where useful.',
      bullets: [
        'Recognition and enforcement under Brussels I bis (EOP, EAPO)',
        'Enforcement proceedings before Italian courts',
        'Every document protected by Italian professional secrecy',
      ],
    },
  },
  switzerland: {
    code: 'switzerland',
    label: 'Switzerland',
    short: 'Switzerland',
    accent: '#D52B1E',
    accentClass: 'accent-ch',
    hero: {
      overline: 'International practice · Switzerland',
      title: ['Recovering claims in Italy', 'for Swiss businesses.'],
      lede:
        "From Ticino manufacturers to Zurich private banks, Swiss enterprises look south. When an Italian counterparty stops paying, the enforcement leg is Italian — and so is our practice.",
    },
    bottleneck:
      "Switzerland sits outside the EU framework, so recognition of Swiss judgments in Italy runs under the Lugano Convention, which retains a declaration-of-enforceability step. We anticipate the procedural nuances and prepare the file so that Italian tribunals accept it without procedural challenges.",
    trust: {
      title: 'Why instruct an Italian lawyer',
      body:
        'Swiss watchmakers, precision engineering firms and financial intermediaries increasingly conclude significant contracts with Italian counterparts. When payment collapses, our Italian practice manages the entire local procedural leg — Verona is 200 km from Chiasso.',
      bullets: [
        'Recognition and declaration of enforceability under the Lugano Convention',
        'Enforcement proceedings before Italian courts',
        'Every document protected by Italian professional secrecy',
      ],
    },
  },
  netherlands: {
    code: 'netherlands',
    label: 'Netherlands',
    short: 'Netherlands',
    accent: '#FF6900',
    accentClass: 'accent-nl',
    hero: {
      overline: 'International practice · Netherlands',
      title: ['Recovering claims in Italy', 'for Dutch businesses.'],
      lede:
        'A logistics-heavy corridor with the Italian market makes Dutch enterprises frequent claimants against Italian counterparties. Our Italian practice handles the local recovery end-to-end.',
    },
    bottleneck:
      'Italian civil procedure moves at its own tempo. We deliver documented, timestamped, reproducible steps that your legal department can circulate internally.',
    trust: {
      title: 'Why instruct an Italian lawyer',
      body:
        'Rotterdam logistics operators, Amsterdam distributors and Eindhoven industrial suppliers regularly trade with Italian counterparts. When payment collapses, the file is Italian. Our practice is Italian — Verona-based, coordinated with Dutch counsel where useful.',
      bullets: [
        'Recognition and enforcement under Brussels I bis (EOP, EAPO)',
        'Enforcement proceedings before Italian courts',
        'Every document protected by Italian professional secrecy',
      ],
    },
  },
};

export const COUNTRY_LIST = Object.values(COUNTRIES);
